import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import ProtectedRoute from '@/components/ProtectedRoute'
import {
  Target,
  Users,
  TrendingUp,
  Award,
  CheckCircle,
  AlertTriangle,
  BarChart3,
  Calendar,
  UserCheck,
  ArrowUpRight,
  ArrowDownRight,
  Plus
} from 'lucide-react'

interface TeamMemberTarget {
  id: string
  name: string
  role: string
  department: string
  targets: {
    revenue: { target: number; actual: number; progress: number }
    performance: { target: number; actual: number; progress: number }
    development: { target: number; actual: number; progress: number }
    teamGrowth: { target: number; actual: number; progress: number }
  }
  overallProgress: number
  status: 'on-track' | 'at-risk' | 'behind'
}

// Mock data - replace with real API call
const mockTeamTargets: TeamMemberTarget[] = [
  {
    id: '1',
    name: 'João Silva',
    role: 'Coordenador de Projetos',
    department: 'Comercial',
    targets: {
      revenue: { target: 500000, actual: 425000, progress: 85 },
      performance: { target: 90, actual: 88, progress: 98 },
      development: { target: 4, actual: 3, progress: 75 },
      teamGrowth: { target: 2, actual: 1, progress: 50 }
    },
    overallProgress: 77,
    status: 'on-track'
  },
  {
    id: '2',
    name: 'Maria Santos',
    role: 'Coordenadora de Marketing',
    department: 'Marketing',
    targets: {
      revenue: { target: 300000, actual: 280000, progress: 93 },
      performance: { target: 85, actual: 92, progress: 108 },
      development: { target: 3, actual: 3, progress: 100 },
      teamGrowth: { target: 1, actual: 2, progress: 200 }
    },
    overallProgress: 100,
    status: 'on-track'
  },
  {
    id: '3',
    name: 'Pedro Oliveira',
    role: 'Coordenador Técnico',
    department: 'Tecnologia',
    targets: {
      revenue: { target: 400000, actual: 220000, progress: 55 },
      performance: { target: 80, actual: 72, progress: 90 },
      development: { target: 5, actual: 2, progress: 40 },
      teamGrowth: { target: 3, actual: 1, progress: 33 }
    },
    overallProgress: 55,
    status: 'at-risk'
  }
]

function MetasGestaoContent() {
  const { user } = useAuth()
  const [teamTargets, setTeamTargets] = useState<TeamMemberTarget[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'on-track' | 'at-risk' | 'behind'>('all')

  useEffect(() => {
    // Simulate API call
    const loadTeamTargets = async () => {
      setIsLoading(true)
      // In production, fetch from API based on user's team
      setTimeout(() => {
        setTeamTargets(mockTeamTargets)
        setIsLoading(false)
      }, 800)
    }

    loadTeamTargets()
  }, [user])

  const filteredTargets = teamTargets.filter(target =>
    selectedFilter === 'all' ? true : target.status === selectedFilter
  )

  const teamSummary = {
    totalMembers: teamTargets.length,
    onTrack: teamTargets.filter(t => t.status === 'on-track').length,
    atRisk: teamTargets.filter(t => t.status === 'at-risk').length,
    behind: teamTargets.filter(t => t.status === 'behind').length,
    avgProgress: Math.round(
      teamTargets.reduce((sum, t) => sum + t.overallProgress, 0) / teamTargets.length || 0
    )
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0
    }).format(value)
  }

  if (isLoading) {
    return (
      
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-brand-gray-light font-body">Carregando metas de gestão...</p>
          </div>
        </div>
      
    )
  }

  return (
    
      <div className="space-y-8 animate-fade-in-up">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
              Metas de Gestão
            </h1>
            <p className="text-brand-gray-light font-body text-lg">
              Acompanhamento das metas e desempenho da equipe
            </p>
          </div>

          <button className="btn-primary flex items-center gap-2 self-start lg:self-auto">
            <Plus size={20} />
            Definir Novas Metas
          </button>
        </div>

        {/* Summary Cards */}
        <div className="dashboard-grid lg:grid-cols-4">
          <div className="card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-monumenta bg-brand-primary/10">
                <Users className="w-6 h-6 text-brand-primary" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-display font-bold text-white">
                {teamSummary.totalMembers}
              </div>
              <p className="text-sm text-brand-gray">
                Membros da Equipe
              </p>
            </div>
          </div>

          <div className="card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-monumenta bg-success/10">
                <CheckCircle className="w-6 h-6 text-success" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-display font-bold text-white">
                {teamSummary.onTrack}
              </div>
              <p className="text-sm text-brand-gray">
                No Caminho Certo
              </p>
            </div>
          </div>

          <div className="card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-monumenta bg-warning/10">
                <AlertTriangle className="w-6 h-6 text-warning" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-display font-bold text-white">
                {teamSummary.atRisk}
              </div>
              <p className="text-sm text-brand-gray">
                Em Risco
              </p>
            </div>
          </div>

          <div className="card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-monumenta bg-brand-blue/10">
                <TrendingUp className="w-6 h-6 text-brand-blue" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-display font-bold text-white">
                {teamSummary.avgProgress}%
              </div>
              <p className="text-sm text-brand-gray">
                Progresso Médio
              </p>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="card">
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { key: 'all', label: 'Todos', count: teamSummary.totalMembers },
              { key: 'on-track', label: 'No Caminho', count: teamSummary.onTrack },
              { key: 'at-risk', label: 'Em Risco', count: teamSummary.atRisk },
              { key: 'behind', label: 'Atrasados', count: teamSummary.behind }
            ].map(filter => (
              <button
                key={filter.key}
                onClick={() => setSelectedFilter(filter.key as any)}
                className={`px-4 py-2 rounded-monumenta text-sm font-medium transition-all duration-200 ${
                  selectedFilter === filter.key
                    ? 'bg-brand-primary text-white'
                    : 'bg-brand-dark-lighter text-brand-gray hover:text-white hover:bg-brand-dark'
                }`}
              >
                {filter.label} ({filter.count})
              </button>
            ))}
          </div>

          {/* Team Members Grid */}
          <div className="space-y-6">
            {filteredTargets.map(member => (
              <div key={member.id} className="card-interactive p-6">
                {/* Member Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-primary to-brand-blue flex items-center justify-center text-white font-display font-bold text-lg">
                      {member.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-bold text-white">
                        {member.name}
                      </h3>
                      <p className="text-sm text-brand-gray">
                        {member.role} • {member.department}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      member.status === 'on-track' ? 'bg-success/20 text-success' :
                      member.status === 'at-risk' ? 'bg-warning/20 text-warning' :
                      'bg-error/20 text-error'
                    }`}>
                      {member.status === 'on-track' ? 'No Caminho' :
                       member.status === 'at-risk' ? 'Em Risco' : 'Atrasado'}
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-display font-bold text-white">
                        {member.overallProgress}%
                      </div>
                      <div className="text-xs text-brand-gray">Progresso Geral</div>
                    </div>
                  </div>
                </div>

                {/* Progress Bars */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Revenue Target */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-brand-gray-light flex items-center gap-2">
                        <BarChart3 className="w-4 h-4" />
                        Receita
                      </span>
                      <span className="text-sm font-medium text-white">
                        {member.targets.revenue.progress}%
                      </span>
                    </div>
                    <div className="w-full bg-brand-dark-lighter rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          member.targets.revenue.progress >= 80 ? 'bg-success' :
                          member.targets.revenue.progress >= 60 ? 'bg-warning' : 'bg-error'
                        }`}
                        style={{ width: `${Math.min(member.targets.revenue.progress, 100)}%` }}
                      />
                    </div>
                    <div className="text-xs text-brand-gray">
                      {formatCurrency(member.targets.revenue.actual)} / {formatCurrency(member.targets.revenue.target)}
                    </div>
                  </div>

                  {/* Performance Target */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-brand-gray-light flex items-center gap-2">
                        <Award className="w-4 h-4" />
                        Performance
                      </span>
                      <span className="text-sm font-medium text-white">
                        {member.targets.performance.progress}%
                      </span>
                    </div>
                    <div className="w-full bg-brand-dark-lighter rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          member.targets.performance.progress >= 80 ? 'bg-success' :
                          member.targets.performance.progress >= 60 ? 'bg-warning' : 'bg-error'
                        }`}
                        style={{ width: `${Math.min(member.targets.performance.progress, 100)}%` }}
                      />
                    </div>
                    <div className="text-xs text-brand-gray">
                      {member.targets.performance.actual}% / {member.targets.performance.target}%
                    </div>
                  </div>

                  {/* Development Target */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-brand-gray-light flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        Desenvolvimento
                      </span>
                      <span className="text-sm font-medium text-white">
                        {member.targets.development.progress}%
                      </span>
                    </div>
                    <div className="w-full bg-brand-dark-lighter rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          member.targets.development.progress >= 80 ? 'bg-success' :
                          member.targets.development.progress >= 60 ? 'bg-warning' : 'bg-error'
                        }`}
                        style={{ width: `${Math.min(member.targets.development.progress, 100)}%` }}
                      />
                    </div>
                    <div className="text-xs text-brand-gray">
                      {member.targets.development.actual} / {member.targets.development.target} cursos
                    </div>
                  </div>

                  {/* Team Growth Target */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-brand-gray-light flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Crescimento
                      </span>
                      <span className="text-sm font-medium text-white">
                        {member.targets.teamGrowth.progress}%
                      </span>
                    </div>
                    <div className="w-full bg-brand-dark-lighter rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          member.targets.teamGrowth.progress >= 80 ? 'bg-success' :
                          member.targets.teamGrowth.progress >= 60 ? 'bg-warning' : 'bg-error'
                        }`}
                        style={{ width: `${Math.min(member.targets.teamGrowth.progress, 100)}%` }}
                      />
                    </div>
                    <div className="text-xs text-brand-gray">
                      {member.targets.teamGrowth.actual} / {member.targets.teamGrowth.target} contratações
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {filteredTargets.length === 0 && (
              <div className="text-center py-12">
                <AlertTriangle className="w-12 h-12 text-brand-gray mx-auto mb-4" />
                <p className="text-brand-gray-light font-body">
                  Nenhum membro encontrado com o filtro selecionado
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    
  )
}

export default function MetasGestaoPage() {
  return (
    <ProtectedRoute>
      <MetasGestaoContent />
    </ProtectedRoute>
  )
}
